import useAuth from "@/components/auth/useAuth"
import formatTimeAgo from "@/components/common/formatTimeAgo"
import {
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Grid,
  Tooltip,
  Paper,
  Switch,
  FormControlLabel
} from "@material-ui/core"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import CloseIcon from "@material-ui/icons/Close"
import TrackChangesIcon from "@material-ui/icons/TrackChanges"
import React, { useCallback, useEffect, useState } from "react"
import { useLocation, useHistory } from "react-router-dom"

import ChangeOrg from "../ChangeOrg"

interface RecentPage {
  id: number
  title: string
  url: string
  timestamp: string
  shortUrl: string
  orgName?: string
  orgRef?: string
}

const STORAGE_KEY = "recentPages"
const TRACKING_ENABLED_KEY = "trackingEnabled"
const MAX_GRIDS = 15

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2)
  },
  orgText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontWeight: 500
  },
  card: {
    padding: theme.spacing(2),
    cursor: "pointer",
    transition: "transform 0.2s",
    "&:hover": {
      boxShadow: theme.shadows[4]
    }
  },
  cardBlurred: {
    padding: theme.spacing(2),
    cursor: "not-allowed",
    transition: "transform 0.2s"
  },
  blurredSection: {
    filter: "blur(2px)",
    opacity: 0.6
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  cardTitle: {
    fontWeight: 500
  },
  cardTime: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem"
  },
  cardUrl: {
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  dialogContentWrapper: {
    position: "relative"
  }
}))

const RecentPagesTracker: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const [recentPages, setRecentPages] = useState<RecentPage[]>([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isTrackingEnabled, setIsTrackingEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem(TRACKING_ENABLED_KEY)
    return stored === null ? true : stored === "true"
  })
  const location = useLocation()
  const history = useHistory()
  const { user } = useAuth()
  const organisationName = user?.organisation?.name
  const organisationRef = user?.organisation?.ref

  const toggleTracking = () => {
    const newValue = !isTrackingEnabled
    setIsTrackingEnabled(newValue)
    localStorage.setItem(TRACKING_ENABLED_KEY, String(newValue))

    if (!newValue) {
      clearRecentPages()
    }
  }

  const visitPage = useCallback(
    (page: { title: string; url: string }) => {
      if (!isTrackingEnabled) return
      const newPage: RecentPage = {
        id: Date.now(),
        title: page.title,
        url: page.url,
        timestamp: new Date().toISOString(),
        shortUrl:
          page.url.length > 60 ? page.url.substring(0, 60) + "..." : page.url,
        orgName: organisationName,
        orgRef: organisationRef
      }

      const storedPages = localStorage.getItem(STORAGE_KEY)
      const existing = storedPages
        ? JSON.parse(storedPages).filter(
            (p: RecentPage) => p.url !== page.url || p.orgRef !== newPage.orgRef
          )
        : []

      const updated = [newPage, ...existing].slice(0, MAX_GRIDS)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      setRecentPages(updated)
    },
    [isTrackingEnabled, organisationName, organisationRef]
  )

  useEffect(() => {
    if (!isTrackingEnabled) return
    const currentPath = location.pathname

    const getDynamicTitle = (path: string): string => {
      const segments = path.split("/").filter(Boolean)
      if (segments.length === 0) return "Dashboard"
      const last = segments[segments.length - 1]
      return (
        last.charAt(0).toUpperCase() +
        last.slice(1).replace(/-/g, " ") +
        " Page"
      )
    }

    const title = getDynamicTitle(currentPath)
    visitPage({ title, url: currentPath })
  }, [location, isTrackingEnabled, visitPage])

  useEffect(() => {
    const storedPages = localStorage.getItem(STORAGE_KEY)
    if (storedPages) {
      setRecentPages(JSON.parse(storedPages))
    }
  }, [])

  const clearRecentPages = () => {
    localStorage.removeItem(STORAGE_KEY)
    setRecentPages([])
  }
  const handleCardClick = (page: RecentPage, isOrgMismatch: boolean) => {
    if (!isOrgMismatch) {
      setIsDialogOpen(false)
      history.push(page.url)
    }
  }

  return (
    <>
      <IconButton
        color="inherit"
        onClick={() => setIsDialogOpen(true)}
        title="Recent Pages"
      >
        <Badge
          badgeContent={recentPages.length > 9 ? "9+" : recentPages.length}
          color="secondary"
        >
          <TrackChangesIcon fontSize="medium" />
        </Badge>
      </IconButton>

      <Dialog
        fullScreen
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      >
        <DialogTitle>
          <Grid className={classes.header}>
            <Grid>
              <Typography variant="h6" style={{ marginLeft: 8 }}>
                Recent Pages
              </Typography>
              <Typography
                variant="body2"
                style={{ marginLeft: 8, color: theme.palette.text.secondary }}
              >
                Quickly go back to pages you visited recently ...
              </Typography>
            </Grid>
            <Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={isTrackingEnabled}
                    onChange={toggleTracking}
                    color="primary"
                  />
                }
                label="Track Recent Pages"
              />
              {recentPages.length > 0 && (
                <Button color="primary" onClick={clearRecentPages}>
                  CLEAR ALL
                </Button>
              )}
              <IconButton onClick={() => setIsDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <div className={classes.dialogContentWrapper}>
          <DialogContent className={classes.root}>
            {user?.organisation?.name && user?.organisation?.ref && (
              <Typography className={classes.orgText} variant="subtitle2">
                <strong>Current Org:</strong> {user.organisation.name} (
                {user.organisation.ref})
              </Typography>
            )}

            <Grid container spacing={2}>
              {recentPages.map(page => {
                const isOrgMismatch = page.orgRef !== user?.organisation?.ref
                const shouldShowTooltip = page.url.length > 60

                return (
                  <Grid item xs={12} sm={6} md={4} key={page.id}>
                    <Paper
                      elevation={2}
                      onClick={() => handleCardClick(page, isOrgMismatch)}
                      className={
                        isOrgMismatch ? classes.cardBlurred : classes.card
                      }
                    >
                      <Grid className={classes.titleRow}>
                        <Grid>
                          <Typography
                            className={`${classes.cardTitle} ${
                              isOrgMismatch ? classes.blurredSection : ""
                            }`}
                            variant="subtitle1"
                          >
                            {page.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{
                              color: isOrgMismatch
                                ? theme.palette.error.main
                                : theme.palette.primary.main,
                              fontWeight: isOrgMismatch ? "bold" : "normal"
                            }}
                          >
                            {page.orgRef}
                          </Typography>
                        </Grid>
                        <Typography
                          className={`${classes.cardTime} ${
                            isOrgMismatch ? classes.blurredSection : ""
                          }`}
                          variant="caption"
                        >
                          {formatTimeAgo(page.timestamp)}
                        </Typography>
                        {isOrgMismatch && (
                          <Grid>
                            <ChangeOrg />
                          </Grid>
                        )}
                      </Grid>
                      {shouldShowTooltip ? (
                        <Tooltip title={page.url}>
                          <Typography
                            className={`${classes.cardUrl} ${
                              isOrgMismatch ? classes.blurredSection : ""
                            }`}
                            variant="body2"
                          >
                            {page.shortUrl}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          className={`${classes.cardUrl} ${
                            isOrgMismatch ? classes.blurredSection : ""
                          }`}
                          variant="body2"
                        >
                          {page.url}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )
              })}
            </Grid>
          </DialogContent>
        </div>
      </Dialog>
    </>
  )
}

export default RecentPagesTracker
